import React, { useState, useEffect } from "react";
import { RiMapPinLine } from "react-icons/ri";

import api from "../../../../services/api";
import { CustomerLocation } from "../../styles";
import { IAddress, Licensee } from "../../dtos";

export const customerAddressCache = new Map<string, { city?: string; state?: string }>();
const pendingAddressRequests = new Map<string, Promise<{ city?: string; state?: string } | null>>();

export function invalidateCustomerAddressCache(licenseeId?: string) {
  if (licenseeId) {
    customerAddressCache.delete(licenseeId);
  } else {
    customerAddressCache.clear();
  }
}

export function setCustomerAddressCache(licenseeId: string, location: { city?: string; state?: string }) {
  if (licenseeId) {
    customerAddressCache.set(licenseeId, {
      city: location.city?.trim() || "",
      state: location.state?.trim().toUpperCase() || "",
    });
  }
}

export function formatCityState(city?: string, state?: string): string {
  const cleanCity = city?.trim();
  const cleanState = state?.trim().toUpperCase();

  if (cleanCity && cleanState) {
    return `${cleanCity}/${cleanState}`;
  }
  if (cleanCity) {
    return cleanCity;
  }
  if (cleanState) {
    return cleanState;
  }
  return "-";
}

const MAX_CONCURRENT_ADDRESS_REQUESTS = 4;
let activeAddressRequests = 0;
const addressRequestQueue: Array<() => void> = [];

function runWithConcurrencyLimit<T>(fn: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const execute = () => {
      activeAddressRequests++;
      fn()
        .then(resolve, reject)
        .finally(() => {
          activeAddressRequests--;
          const next = addressRequestQueue.shift();
          if (next) next();
        });
    };

    if (activeAddressRequests < MAX_CONCURRENT_ADDRESS_REQUESTS) {
      execute();
    } else {
      addressRequestQueue.push(execute);
    }
  });
}

export async function fetchRegistrationAddress(
  licenseeId: string
): Promise<{ city?: string; state?: string } | null> {
  if (!licenseeId) return null;

  if (customerAddressCache.has(licenseeId)) {
    return customerAddressCache.get(licenseeId) || null;
  }

  if (pendingAddressRequests.has(licenseeId)) {
    return pendingAddressRequests.get(licenseeId)!;
  }

  const promise = runWithConcurrencyLimit(async () => {
    try {
      const response = await api.get(`/address/all/${licenseeId}`);
      const list: IAddress[] = Array.isArray(response.data) ? response.data : [];
      const regAddress =
        list.find((a: any) => Boolean(a.company_address)) || list[0];

      const result = {
        city: regAddress?.city?.trim() || "",
        state: regAddress?.state?.trim().toUpperCase() || "",
      };
      customerAddressCache.set(licenseeId, result);
      return result;
    } catch {
      return null;
    } finally {
      pendingAddressRequests.delete(licenseeId);
    }
  });

  pendingAddressRequests.set(licenseeId, promise);
  return promise;
}

interface CustomerLocationPinProps {
  customer: Licensee;
}

export function CustomerLocationPin({ customer }: CustomerLocationPinProps) {
  const preloadedAddresses: IAddress[] | undefined =
    (customer as any).address ?? (customer as any).addresses;
  const hasPreloadedAddresses = Array.isArray(preloadedAddresses);

  const preloadedReg =
    hasPreloadedAddresses && preloadedAddresses.length > 0
      ? preloadedAddresses.find((a: any) => Boolean(a.company_address)) || preloadedAddresses[0]
      : null;

  const initialCity =
    preloadedReg?.city || customerAddressCache.get(customer.id)?.city || customer.city;
  const initialState =
    preloadedReg?.state || customerAddressCache.get(customer.id)?.state || customer.state;

  const [location, setLocation] = useState<{ city?: string; state?: string }>({
    city: initialCity,
    state: initialState,
  });

  useEffect(() => {
    let isMounted = true;

    if (hasPreloadedAddresses) {
      const resolved = {
        city: preloadedReg?.city?.trim() || "",
        state: preloadedReg?.state?.trim().toUpperCase() || "",
      };
      customerAddressCache.set(customer.id, resolved);
      setLocation(resolved);
      return;
    }

    if (customerAddressCache.has(customer.id)) {
      const cached = customerAddressCache.get(customer.id);
      if (cached) {
        setLocation(cached);
      }
      return;
    }

    fetchRegistrationAddress(customer.id).then((result) => {
      if (isMounted && result && (result.city || result.state)) {
        setLocation(result);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [customer.id, hasPreloadedAddresses, preloadedReg]);

  const displayCity = location.city || customer.city;
  const displayState = location.state || customer.state;

  return (
    <CustomerLocation>
      <RiMapPinLine size={12} />
      <span>{formatCityState(displayCity, displayState)}</span>
    </CustomerLocation>
  );
}
